/// <amd-module name="Work/Views/MyWork/View/MyWorkView" />
import * as React from "react";

import { Components, VssComponent, IVssComponentContext, css } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { getAssetLocation } from "VSS/Platform/Location";
import { FormatComponent } from "VSS/Platform/Components/Format";

import { ITfsPageService } from "Tfs/Platform/Page";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";

import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints, ScreenSize } from "VSS/Core/Util/Screen";
import { equals } from "VSS/Core/Util/String";

import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Table, ITableColumn, TwoLineTableCell, SimpleTableCell, ITableBreakpoint, ITableRow, TableRow } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import * as MyWorkResources from "Work/Views/MyWork/Resources";
import { MyWorkViewComponentProps } from "./MyWorkView.Props";
import { QueryOption, WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";

import { WorkItemTrackingDataProvider, WorkItemStateProvider, WorkItemTypeIconProvider } from "Wit/UI/Data";
import { WorkItemState } from "Wit/UI/WorkItemState";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

import { IMyWorkProvider, ProviderState, IMyWorkProviderStateChangeArgs } from "DataProviders/IMyWorkProvider";
import { MyWorkProvider } from "DataProviders/MyWorkProvider";

import { activityDateFieldName } from "DataProviders/MyWorkProviderBase";

const enum PivotIdentifier {
    assignedToMe = "assignedToMe",
    myTeams = "myTeams"
}

/**
 * View component for the work items 'Me' page.
 */
export class MyWorkViewComponent extends VssComponent<MyWorkViewComponentProps, {}> {
    private static readonly AREA = "wit";
    private static readonly FLYOUT_PERF_SCENARIO_NAME = "MyWorkFlyout-Initialize";
    private static readonly PAGE_PERF_SCENARIO_NAME = "MyWorkPage-Initialize";

    private workItemListProviders: { [key: string]: IMyWorkProvider } = {};
    private _workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    private _tableColumns: ITableColumn<WorkItem>[];
    private _projectName: string;
    private _performanceScenarioName: string;
    private _isPageInteractive: boolean;

    constructor(props: MyWorkViewComponentProps, context: IVssComponentContext) {
        super(props, context);

        this._performanceScenarioName = !!this.props.extendedDisplay
            ? MyWorkViewComponent.PAGE_PERF_SCENARIO_NAME
            : MyWorkViewComponent.FLYOUT_PERF_SCENARIO_NAME;
        // if extendedDisplay - should log perf scenario as TTI.
        this._isPageInteractive = !!this.props.extendedDisplay;

        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        // Only start perf measurement for FPS switch and the flyout
        if (perfService.isFastPageSwitchScenario() || !this._isPageInteractive) {
            perfService.startScenario(this._performanceScenarioName, this._isPageInteractive);
        }

        this._workItemTrackingDataProvider = new WorkItemTrackingDataProvider(this.context.pageContext);

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this._projectName = tfsPageData.project.name;
        } else {
            this._projectName = "";
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();

        // initialize work providers
        this.getProvider(this.context, PivotIdentifier.assignedToMe).initialize();
    }

    public render(): JSX.Element {
        if (!this._tableColumns) {
            this._buildColumns();
        }
        const provider = this.getProvider(this.context, PivotIdentifier.assignedToMe);

        if (provider.getState() === ProviderState.LoadedZeroData) {
            return this.getZeroDataContent(
                "ms.vss-work-web/my-work-content/Content/zerodata-your-work-in-one-place.svg",
                MyWorkResources.ZeroData_AssignedToMe_Primary,
                MyWorkResources.ZeroData_AssignedToMe_Secondary,
                MyWorkResources.ZeroData_LinkText,
                MyWorkResources.ZeroData_AssignedToMe_Link
            );
        }

        if (provider.getState() === ProviderState.Uninitialized || provider.getState() === ProviderState.Error) {
            return <div />;
        }

        if (provider.getState() === ProviderState.Loading) {
            return this.getSpinner();
        }

        const workItemProvider = new ArrayItemProvider(provider.getWorkItems());

        const columns: ITableColumn<WorkItem>[] = this._tableColumns;
        let tableBreakpoints: ITableBreakpoint[];

        if (this.props.extendedDisplay) {
            tableBreakpoints = [
                {
                    breakpoint: ScreenBreakpoints.xsmall,
                    columnWidths: [15, 0, -100, 0, 0]
                },
                {
                    breakpoint: ScreenBreakpoints.small,
                    columnWidths: [15, -100, 0, 90, 200]
                }
            ];
        } else {
            tableBreakpoints = [
                {
                    breakpoint: ScreenBreakpoints.xsmall,
                    columnWidths: [15, -100, 0, 90, 0]
                }
            ];
        }

        return (
            <div className={css("mywork-panel flex-column flex-grow", this.props.className)}>
                <ScreenSizeObserver>
                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                        const smallScreen = screenSizeProps.screenSize === ScreenSize.xsmall;

                        return (
                            <>
                                <div className="work-items-table-label body-m secondary-text">{MyWorkResources.AssignedToMe}</div>
                                <Table
                                    className="work-items"
                                    itemProvider={workItemProvider}
                                    columns={columns}
                                    tableBreakpoints={tableBreakpoints}
                                    onActivate={smallScreen ? this.onRowActivate : undefined}
                                />
                                {this.getLimitMessage(provider, MyWorkResources.AssignedToMeQueryLimitExceeded)}
                            </>
                        );
                    }}
                </ScreenSizeObserver>
            </div>
        );
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        for (const key in this.workItemListProviders) {
            if (this.workItemListProviders.hasOwnProperty(key)) {
                const provider = this.workItemListProviders[key];

                if (provider) {
                    provider.unsubscribe(this.onProviderStateChanged);
                }
            }
        }
    }

    private getProvider = (context: IVssComponentContext, pivotKey: PivotIdentifier, queryOption?: QueryOption): IMyWorkProvider => {
        if (this.workItemListProviders[pivotKey]) {
            return this.workItemListProviders[pivotKey];
        }

        let result: IMyWorkProvider;

        switch (pivotKey) {
            case PivotIdentifier.assignedToMe:
                result = new MyWorkProvider(context.pageContext, QueryOption.Doing);
                break;
            default:
                throw new Error(`Unknown data provider: ${pivotKey}`);
        }

        this.workItemListProviders[pivotKey] = result;

        result.subscribe(this.onProviderStateChanged);

        return result;
    };

    private getZeroDataContent(
        resourceName: string,
        primaryText: string,
        secondaryText: string | JSX.Element,
        linkText: string,
        link: string
    ): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, resourceName);
        return (
            <div className="flex flex-column flex-grow">
                <ZeroData
                    imagePath={image}
                    imageAltText={primaryText}
                    primaryText={primaryText}
                    secondaryText={secondaryText}
                    actionText={linkText}
                    actionType={ZeroDataActionType.link}
                    actionHref={link}
                />
            </div>
        );
    }

    private onProviderStateChanged = (providerStateChange: IMyWorkProviderStateChangeArgs, action: string): void => {
        const { previousState, state } = providerStateChange;
        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        // When the provider state changes update the UI
        this.forceUpdate(() => {
            if (previousState === ProviderState.Loading) {
                const startTime = Date.now();
                // end performance scenario in next tick as DetailsList gets rendered asynchronously
                // once we replace it with table component we can remove setTimeout workaround
                setTimeout(() => {
                    const elapsedTime = Date.now() - startTime;
                    // Record perf telemetry about the time it took to load/draw the UI
                    perfService.endScenario(MyWorkViewComponent.AREA, this._performanceScenarioName, this._isPageInteractive, undefined, {
                        state: ProviderState[state],
                        gridRenderTime: elapsedTime
                    });
                }, 0);
            }
        });

        if (state === ProviderState.Error) {
            const bannerService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
            bannerService.addBanner({ level: 2, dismissable: true, message: MyWorkResources.General_Error }, true);
        }
    };

    private onRowActivate = (event: React.SyntheticEvent<HTMLElement, Event>, tableRow: ITableRow<WorkItem>) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("WebPlatform", "Link", {
            source: "wit-my-work-view",
            clickTarget: "work-item-row"
        });
        window.location.href = tableRow.data._links.html;
    };

    private getSpinner(): JSX.Element {
        return (
            <div className="flex flex-column flex-grow mywork-panel">
                <Spinner className="my-work-items-spinner" size={SpinnerSize.large} label={MyWorkResources.Loading} />
            </div>
        );
    }

    private getLimitMessage(provider: IMyWorkProvider, limitMessage: string): JSX.Element | null {
        let limitJsx: JSX.Element | null = null;
        if (provider.getLimitExceeded()) {
            limitJsx = <div className="limit-message">{limitMessage}</div>;
        }

        return limitJsx;
    }

    private _buildColumns() {
        this._tableColumns = [
            {
                id: "icon",
                width: new ObservableValue(15),
                renderCell: (rowindex: number, columnindex: number, tableColumn: ITableColumn<WorkItem>, workItem: WorkItem) => {
                    return (
                        <SimpleTableCell columnIndex={columnindex} className="icon-column" contentClassName="no-h-padding" key={"icon" + rowindex}>
                            <WorkItemTypeIconProvider
                                dataProvider={this._workItemTrackingDataProvider}
                                dataSource={{
                                    projectIdOrName: workItem.fields[CoreFieldRefNames.TeamProject],
                                    workItemTypeName: workItem.fields[CoreFieldRefNames.WorkItemType]
                                }}
                            >
                                {icon => <WorkItemTypeIcon data={icon} />}
                            </WorkItemTypeIconProvider>
                        </SimpleTableCell>
                    );
                }
            } as ITableColumn<WorkItem>,
            {
                id: "title",
                minWidth: 250, // Need to specify this to ensure that we span the available horizontal space
                width: new ObservableValue(-100),
                renderCell: (rowindex: number, columnindex: number, tableColumn: ITableColumn<WorkItem>, workItem: WorkItem) => {
                    const secondaryRowFormat = MyWorkResources.WorkItemsSecondaryLine;
                    const secondaryItems: JSX.Element[] = [];
                    secondaryItems.push(<span key="id">{workItem.id}</span>);
                    secondaryItems.push(
                        <Link href={workItem._links.project} key="project" subtle={true}>
                            {workItem.fields[CoreFieldRefNames.TeamProject]}
                        </Link>
                    );
                    return (
                        <TwoLineTableCell
                            className="main-column"
                            columnIndex={columnindex}
                            key={"title" + rowindex}
                            line1={
                                // if current project === work item project, use FPS
                                <FPSLink
                                    className="work-item-title body-l text-ellipsis"
                                    href={workItem._links.html}
                                    subtle={true}
                                    supportsFPS={equals(this._projectName, workItem.fields[CoreFieldRefNames.TeamProject], true)}
                                    telemetrySource="wit-my-work-view"
                                    telemetryProperties={{ clickTarget: "work-item-title" }}
                                    removeUnderline={true}
                                >
                                    <Tooltip overflowOnly={true} text={workItem.fields[CoreFieldRefNames.Title]}>
                                        <div className="text-ellipsis">{workItem.fields[CoreFieldRefNames.Title]}</div>
                                    </Tooltip>
                                </FPSLink>
                            }
                            line2={
                                <FormatComponent className="secondary-text body-s" format={secondaryRowFormat}>
                                    {secondaryItems}
                                </FormatComponent>
                            }
                        />
                    );
                }
            } as ITableColumn<WorkItem>,
            {
                id: "mobile-title",
                minWidth: 250, // Need to specify this to ensure that we span the available horizontal space
                width: new ObservableValue(-100),
                renderCell: (rowindex: number, columnindex: number, tableColumn: ITableColumn<WorkItem>, workItem: WorkItem) => {
                    return (
                        <TwoLineTableCell
                            className="main-column text-ellipsis"
                            columnIndex={columnindex}
                            key={"mobile-title" + rowindex}
                            line1={
                                <Tooltip overflowOnly={true} text={workItem.fields[CoreFieldRefNames.Title]}>
                                    <div className="text-ellipsis body-l">{workItem.fields[CoreFieldRefNames.Title]}</div>
                                </Tooltip>
                            }
                            line2={
                                <WorkItemStateProvider
                                    dataProvider={this._workItemTrackingDataProvider}
                                    dataSource={{
                                        projectIdOrName: workItem.fields[CoreFieldRefNames.TeamProject],
                                        workItemTypeName: workItem.fields[CoreFieldRefNames.WorkItemType],
                                        stateName: workItem.fields[CoreFieldRefNames.State]
                                    }}
                                >
                                    {stateAndColor => <WorkItemState data={stateAndColor} hideText={false} />}
                                </WorkItemStateProvider>
                            }
                        />
                    );
                }
            } as ITableColumn<WorkItem>,
            {
                id: "state",
                width: new ObservableValue(90),
                renderCell: (rowindex: number, columnindex: number, tableColumn: ITableColumn<WorkItem>, workItem: WorkItem) => {
                    return (
                        <SimpleTableCell className="state-column" columnIndex={columnindex} key={"state" + rowindex}>
                            <WorkItemStateProvider
                                dataProvider={this._workItemTrackingDataProvider}
                                dataSource={{
                                    projectIdOrName: workItem.fields[CoreFieldRefNames.TeamProject],
                                    workItemTypeName: workItem.fields[CoreFieldRefNames.WorkItemType],
                                    stateName: workItem.fields[CoreFieldRefNames.State]
                                }}
                            >
                                {stateAndColor => <WorkItemState data={stateAndColor} hideText={false} />}
                            </WorkItemStateProvider>
                        </SimpleTableCell>
                    );
                }
            } as ITableColumn<WorkItem>,
            {
                id: "LastUpdated",
                width: new ObservableValue(200),
                renderCell: (rowindex: number, columnindex: number, tableColumn: ITableColumn<WorkItem>, workItem: WorkItem) => {
                    return (
                        <SimpleTableCell className="activity-column" columnIndex={columnindex} key={"LastUpdated" + rowindex}>
                            <Tooltip overflowOnly={true} text={workItem.fields[activityDateFieldName].date.toLocaleString(undefined, undefined)}>
                                <div className="flex flex-grow text-ellipsis">{workItem.fields[activityDateFieldName].formattedDate}</div>
                            </Tooltip>
                        </SimpleTableCell>
                    );
                }
            } as ITableColumn<WorkItem>
        ];
    }
}

Components.add("my-work-view", MyWorkViewComponent);